import { useMutation } from "@tanstack/react-query";
import { defineMessages, useIntl } from "react-intl";

import { QueryKeys, queryClient } from "@/api/core";
import { useApi } from "@/hooks";
import toast from "@/toast";

import type { Account, Relationship } from "@/schemas";

interface Variables {
  account: Pick<Account, "id" | "acct">;
}

const messages = defineMessages({
  unfollowSuccess: {
    id: "accounts.unfollow.success",
    defaultMessage: "You have successfully unfollowed @{username}.",
  },
});

function useUnfollowAccount() {
  const api = useApi();
  const intl = useIntl();

  return useMutation({
    mutationFn: (variables: Variables) =>
      api.post<Relationship>(
        `/api/v1/accounts/${variables.account.id}/unfollow`,
      ),
    async onMutate(variables) {
      const accountQueryKey = QueryKeys.accounts.account(variables.account.id);
      const relationshipQueryKey = QueryKeys.relationships.byAccount(
        variables.account.id,
      );

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [accountQueryKey, relationshipQueryKey],
      });

      // Optimistically update the account to the new value.
      const prevAccount = queryClient.getQueryData(accountQueryKey) as Account;
      let nextAccount: Account;
      if (prevAccount) {
        nextAccount = {
          ...prevAccount,
          followers_count: prevAccount.followers_count - 1,
        };
        queryClient.setQueryData(accountQueryKey, nextAccount);
      }

      // Optimistically update the relationship to the new value.
      const prevRelationship = queryClient.getQueryData(
        relationshipQueryKey,
      ) as Relationship;
      let nextRelationship: Relationship;
      if (prevRelationship) {
        nextRelationship = { ...prevRelationship, following: false };
        queryClient.setQueryData(relationshipQueryKey, nextRelationship);
      }

      return { prevAccount, prevRelationship };
    },
    onSuccess(response, variables) {
      const queryKey = QueryKeys.relationships.byAccount(variables.account.id);
      queryClient.setQueryData(queryKey, response.data);

      // Render a toast
      toast.success(
        intl.formatMessage(messages.unfollowSuccess, {
          username: variables.account.acct,
        }),
      );
    },
    onError(error, variables, context) {
      const accountQueryKey = QueryKeys.accounts.account(variables.account.id);
      const relationshipQueryKey = QueryKeys.relationships.byAccount(
        variables.account.id,
      );

      // Return Relationship back to its original state
      if (context) {
        queryClient.setQueryData(accountQueryKey, context.prevAccount);
        queryClient.setQueryData(
          relationshipQueryKey,
          context.prevRelationship,
        );
      }
    },
  });
}

export { useUnfollowAccount };
